import { useState } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LeadFormModal({ isOpen, onClose }) {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    cnpj: "",
    position: "",
    phone: "",
    employees: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClose = () => {
    setIsSuccess(false);
    setFormData({ name: "", email: "", company: "", cnpj: "", position: "", employees: "" });
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.company || !formData.cnpj || !formData.position || !formData.phone || !formData.employees) {
      toast.error("Todos os campos são obrigatórios");
      return;
    }
    
    setIsLoading(true);

    try {
      await axios.post(`${API}/leads`, {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        cnpj: formData.cnpj,
        position: formData.position,
        phone: formData.phone,
        employees: formData.employees,
      });
      setIsSuccess(true);
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao realizar cadastro. Tente novamente.";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="bg-[#1C1F26] border border-[#2A2F38] text-[#E5E7EB] max-w-md sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="lead-form-modal">
          <div className="text-center py-4" data-testid="lead-form-success">
            <div className="w-14 h-14 mx-auto mb-6 flex items-center justify-center bg-[#0E3A46] rounded-full">
              <svg className="h-7 w-7 text-[#F59E0B]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            </div>

            <h3 className="font-heading text-xl font-bold text-[#E5E7EB] mb-6">
              Cadastro recebido com sucesso.
            </h3>

            <div className="bg-[#0F1115] border border-[#2A2F38] rounded-sm p-5 mb-6 text-left space-y-4">
              <p className="text-sm text-[#9CA3AF]">
                Para dar continuidade à liberação do seu período de avaliação, envie um e-mail para:
              </p>

              <div className="text-center">
                <a
                  href="mailto:contato@kpaction.com.br"
                  className="inline-flex items-center gap-2 text-[#F59E0B] hover:text-[#FBBF24] transition-colors font-medium"
                  data-testid="success-email-link"
                >
                  contato@kpaction.com.br
                </a>
              </div>

              <p className="text-sm text-[#9CA3AF]">
                Informando que realizou a solicitação de teste do KpAction e o nome da empresa cadastrada.
              </p>

              <div className="border-t border-[#2A2F38] pt-4">
                <p className="text-sm text-[#E5E7EB]">
                  O link para download do executável será enviado após a validação do seu cadastro.
                </p>
              </div>
            </div>

            <Button
              onClick={handleClose}
              variant="outline"
              className="border-[#2A2F38] text-[#9CA3AF] hover:bg-[#2A2F38] hover:text-[#E5E7EB]"
              data-testid="success-close-btn"
            >
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-[#1C1F26] border border-[#2A2F38] text-[#E5E7EB] max-w-md sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="lead-form-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-bold text-center">
            <span className="text-[#F59E0B]">Testar</span> KpAction Grátis
          </DialogTitle>
        </DialogHeader>

        <div className="text-center mb-6">
          <p className="text-sm text-[#9CA3AF]">
            Preencha seus dados para iniciar o teste gratuito de 30 dias.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium text-[#9CA3AF] uppercase tracking-wider">
              Nome Completo *
            </Label>
            <Input
              id="name"
              name="name"
              type="text"
              required
              value={formData.name}
              onChange={handleChange}
              placeholder="Seu nome completo"
              className="bg-[#0F1115] border-[#2A2F38] text-[#E5E7EB] placeholder:text-[#6B7280] focus:border-[#F59E0B] focus:ring-[#F59E0B]"
              data-testid="lead-form-name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium text-[#9CA3AF] uppercase tracking-wider">
              Email Corporativo *
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder="seu@empresa.com"
              className="bg-[#0F1115] border-[#2A2F38] text-[#E5E7EB] placeholder:text-[#6B7280] focus:border-[#F59E0B] focus:ring-[#F59E0B]"
              data-testid="lead-form-email"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="company" className="text-sm font-medium text-[#9CA3AF] uppercase tracking-wider">
              Razão Social *
            </Label>
            <Input
              id="company"
              name="company"
              type="text"
              required
              value={formData.company}
              onChange={handleChange}
              placeholder="Razão social da empresa"
              className="bg-[#0F1115] border-[#2A2F38] text-[#E5E7EB] placeholder:text-[#6B7280] focus:border-[#F59E0B] focus:ring-[#F59E0B]"
              data-testid="lead-form-company"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="cnpj" className="text-sm font-medium text-[#9CA3AF] uppercase tracking-wider">
              CNPJ *
            </Label>
            <Input
              id="cnpj"
              name="cnpj"
              type="text"
              required
              value={formData.cnpj}
              onChange={handleChange}
              placeholder="00.000.000/0000-00"
              className="bg-[#0F1115] border-[#2A2F38] text-[#E5E7EB] placeholder:text-[#6B7280] focus:border-[#F59E0B] focus:ring-[#F59E0B]"
              data-testid="lead-form-cnpj"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="position" className="text-sm font-medium text-[#9CA3AF] uppercase tracking-wider">
              Cargo *
            </Label>
            <Input
              id="position"
              name="position"
              type="text"
              required
              value={formData.position}
              onChange={handleChange}
              placeholder="Seu cargo na empresa"
              className="bg-[#0F1115] border-[#2A2F38] text-[#E5E7EB] placeholder:text-[#6B7280] focus:border-[#F59E0B] focus:ring-[#F59E0B]"
              data-testid="lead-form-position"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone" className="text-sm font-medium text-[#9CA3AF] uppercase tracking-wider">
              Telefone *
            </Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              required
              value={formData.phone}
              onChange={handleChange}
              placeholder="(00) 00000-0000"
              className="bg-[#0F1115] border-[#2A2F38] text-[#E5E7EB] placeholder:text-[#6B7280] focus:border-[#F59E0B] focus:ring-[#F59E0B]"
              data-testid="lead-form-phone"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="employees" className="text-sm font-medium text-[#9CA3AF] uppercase tracking-wider">
              Número de Funcionários *
            </Label>
            <Select 
              value={formData.employees} 
              onValueChange={(value) => handleSelectChange("employees", value)}
              required
            >
              <SelectTrigger 
                className="bg-[#0F1115] border-[#2A2F38] text-[#E5E7EB] focus:border-[#F59E0B] focus:ring-[#F59E0B]"
                data-testid="lead-form-employees"
              >
                <SelectValue placeholder="Selecione *" />
              </SelectTrigger>
              <SelectContent className="bg-[#1C1F26] border-[#2A2F38]">
                <SelectItem value="1-10" className="text-[#E5E7EB] focus:bg-[#2A2F38]">1-10</SelectItem>
                <SelectItem value="11-25" className="text-[#E5E7EB] focus:bg-[#2A2F38]">11-25</SelectItem>
                <SelectItem value="26-50" className="text-[#E5E7EB] focus:bg-[#2A2F38]">26-50</SelectItem>
                <SelectItem value="50+" className="text-[#E5E7EB] focus:bg-[#2A2F38]">Mais de 50</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-[#F59E0B] text-black font-bold uppercase tracking-wide hover:bg-[#D97706] transition-all rounded-sm py-4 mt-6 shadow-glow hover:shadow-glow-strong"
            data-testid="lead-form-submit"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                Iniciar Teste Gratuito
                <ArrowRight className="ml-2 h-5 w-5" />
              </>
            )}
          </Button>
        </form>

        <p className="text-xs text-[#6B7280] text-center mt-4">
          Ao se cadastrar, você concorda com nossa{" "}
          <a href="#privacy" className="text-[#F59E0B] hover:underline" onClick={handleClose}>
            Política de Privacidade
          </a>
          .
        </p>
      </DialogContent>
    </Dialog>
  );
}
